import { IUser } from "../Users"
import { useXHR } from "@sembark-travel/xhr"
import useSWR from "swr"

export type THotelImagePlacementOptions = {
	id: "left" | "right"
	name: string
}
type THotelImageVariantConfig = {
	id: "image_placement"
	name: string
	value: "left" | "right"
	options: Array<THotelImagePlacementOptions>
}
export type THotelImageVariant = {
	id: "image_and_text"
	name: string
	configurations: Array<THotelImageVariantConfig>
}

type THotelSelectedConfiguration = Omit<
	THotelImageVariant["configurations"][number],
	"options"
>

export type THotelSelectedImageVariant = {
	id: "image_and_text"
	name: string
	configurations: THotelSelectedConfiguration[]
}

export type THotelTableVariant = {
	id: "table_view"
	name: string
	configurations: []
}

export type TFontFamily = {
	id: string
	name: string
	url: string | null
}

export type TVariable = {
	id: string
	name: string
	description: string
	placeholder: string
}

export const BORDER_WIDTH_OPTIONS = [0, 4, 8, 12, 16, 20]

type TEmailPdfTemplate = {
	id: number
	name: string
	greetings: string
	subject_line?: string | null
	settings: {
		fontFamily: TFontFamily
		justifiedText: 0 | 1
		watermarkText?: string
		borderBottomStyle?: string
		borderBottomWidth?: number
		borderLeftStyle?: string
		borderLeftWidth?: number
		borderRightStyle?: string
		borderRightWidth?: number
		borderTopStyle?: string
		borderTopWidth?: number
		borderStyle?: string
	}
	created_at: string
	updated_at?: string
	created_by?: IUser
	updated_by?: IUser
	section_title_settings?: {
		background: "fill" | "dark_fill" | "none"
		border:
			| "top"
			| "right"
			| "bottom"
			| "left"
			| "horizontal"
			| "vertical"
			| "all"
			| "none"
		rounded: "top" | "right" | "bottom" | "left" | "all" | "none"
		textAlign: "left" | "right" | "center" | "auto"
	}
	transport_and_activity_section_settings: {
		hide_transport_summary_for_same_cabs: 0 | 1
		variant?: string
		terms?: string
		title?: string
	}
	daywise_schedule_section_settings?: {
		title?: string
		hide_travel_distance?: 0 | 1 | "0" | "1"
		show_same_day_service_together?: 0 | 1 | "0" | "1"
	}
	terms_section_settings: {
		title: string
	}
	other_info_section_settings: {
		title: string
	}
	pricing_section_settings?: {
		hide_quote_price?: 0 | 1 | "0" | "1"
		show_package_price_below_hotel?: 0 | 1 | "0" | "1"
		show_component_wise_prices?: 0 | 1 | "0" | "1"
	}
	hotel_section_settings?: {
		title?: string
		similar_word_in_hotel?: 0 | 1 | "0" | "1"
		show_package_price_below_hotel?: 0 | 1 | "0" | "1"
		variant: THotelSelectedImageVariant | THotelTableVariant
		variants: Array<THotelImageVariant | THotelTableVariant>
	}
	section_ordering_settings: Array<{
		order: number
		title: string
		section: string
		hide?: 1 | 0 | "1" | "0"
	}>
}

type TSectionOrdering = Array<{
	order: number
	section: string
	title: string
}>

export type TTripQuoteWhatsAppTemplate = {
	name: string
	greetings: string
	conversion_greetings?: string
	after_conversion_updates_greetings?: string
	section_ordering_settings: TSectionOrdering
	variables: Array<{ name: string; description: string; placeholder: string }>
}

export type TBrandWiseColors = {
	brand_wise_color_shades: Array<{
		id: number
		name: string
		color_shades?: {
			[key: string]: string
		}
	}>
}

export function useTripQuoteWhatsAppTemplate() {
	const xhr = useXHR()
	const { data } = useSWR(
		"/trip-quote-whatsapp-templates",
		() =>
			xhr
				.get<{
					data: Array<TTripQuotePdfTemplate>
				}>("/trip-quote-pdf-templates", {
					params: {
						include: ["use_fallback"].filter(Boolean).join(","),
					},
				})
				.then((resp) => resp.data.data[0]),
		{ revalidateIfStale: false }
	)
	return data
}

export type TImageStackedOptions = {
	id: "align_to_service" | "full_width"
	name: string
}
type TImageStackedConfig = {
	id: "image_stacked"
	name: string
	options: Array<TImageStackedOptions>
	value: "align_to_service" | "full_width"
}
export type TStackedVariant = {
	id: "stacked"
	name: string
	configurations: Array<TImageStackedConfig>
}

export type TImageOptions = {
	id: "right" | "left" | "alternate"
	name: string
}
type TSideBySideConfig = {
	id: "image"
	name: string
	options: Array<TImageOptions>
	value: "right" | "left" | "alternate"
}
export type TSidebySideVariant = {
	id: "side_by_side"
	name: string
	configurations: Array<TSideBySideConfig>
}

export type TTripQuoteEmailTemplate = TEmailPdfTemplate & {
	settings: {
		fontFamily: TFontFamily
		justifiedText: 0 | 1
	}
	section_title_settings: {
		background: "fill" | "dark_fill" | "none"
		border:
			| "top"
			| "right"
			| "bottom"
			| "left"
			| "horizontal"
			| "vertical"
			| "all"
			| "none"
		textAlign: "left" | "right" | "center" | "auto"
	}
	transport_and_activity_section_settings: {
		show_activity_start_time: 0 | 1
		hide_transport_summary_for_same_cabs: 0 | 1
		variant: string
		terms?: string
		title?: string
	}
	daywise_schedule_section_settings: {
		title: string
		hide_travel_distance: 0 | 1 | "0" | "1"
		show_same_day_service_together: 0 | 1 | "0" | "1"
	}
}

type TStackedConfigWithoutOptions = Omit<TImageStackedConfig, "options">

type TSideBySideConfigWithoutOptions = Omit<TSideBySideConfig, "options">
type TStackedVariantSelected = {
	id: "stacked"
	name: string
	configurations: TStackedConfigWithoutOptions[]
}

type TSideBySideVariantSelected = {
	id: "side_by_side"
	name: string
	configurations: TSideBySideConfigWithoutOptions[]
}
type TPackagePreviewVariation = "show_details_in_table" | "default"
export type TTripQuotePdfTemplate = TEmailPdfTemplate & {
	settings: {
		fontFamily: TFontFamily
		headingFontFamily: TFontFamily
		justifiedText: 0 | 1
		watermarkText?: string
		watermarkOpacity?: number
		backgroundOpacity?: number
		borderBottomStyle?: string
		borderBottomWidth?: number
		borderLeftStyle?: string
		borderLeftWidth?: number
		borderRightStyle?: string
		borderRightWidth?: number
		borderTopStyle?: string
		borderTopWidth?: number
		borderStyle?: string
		showHeaderBannerImg?: boolean
		showFooterBannerImg?: boolean
		showSingleHeaderFooter?: boolean
		hideBrandingB2B?: boolean
		hideBrandingB2C?: boolean
	}
	section_title_settings: {
		background: "fill" | "dark_fill" | "none"
		border:
			| "top"
			| "right"
			| "bottom"
			| "left"
			| "horizontal"
			| "vertical"
			| "all"
			| "none"
		rounded: "top" | "right" | "bottom" | "left" | "all" | "none"
		textAlign: "left" | "right" | "center" | "auto"
		sectionHeadingFontFamily: TFontFamily
	}
	transport_and_activity_section_settings: {
		hide_transport_summary_for_same_cabs: 0 | 1
		variant?: string
		terms?: string
		title?: string
		show_activity_start_time: 0 | 1 | "0" | "1"
	}
	daywise_schedule_section_settings?: {
		title?: string
		hide_travel_distance: 0 | 1 | "0" | "1"
		show_same_day_service_together: 0 | 1 | "0" | "1"
		variant: TStackedVariantSelected | TSideBySideVariantSelected
		variants: Array<TStackedVariant | TSidebySideVariant>
	}
	package_preview: {
		variant: {
			id: TPackagePreviewVariation
			name: string
			configurations: []
		}
		variants: Array<{
			id: TPackagePreviewVariation
			name: string
			configurations: []
		}>
	}
	hero_section_settings: {
		sales_info_title?: string
	}
	pricing_section_settings: {
		hide_quote_price: 0 | 1 | "0" | "1"
	}
	inclusions_exclusions_section_settings: {
		title: string
	}
}

export type TActivityVoucherTemplate = {
	title: string
}

export type TTripVoucherTemplate = {
	title: string
	show_checkin_checkout_time?: boolean
	section_ordering_settings: Array<{
		order: number
		title: string
		section: string
		hide?: 1 | 0 | "1" | "0"
	}>
}

export type IHotelBookingEnquiryEmailTemplateGet = {
	data: [
		{
			subject_line: string
			greetings: string
		},
	]
}

export type IHotelBookingEnquiryEmailTemplate = {
	subject_line: string
	greetings: string
}

export type IHotelBookingEnquiryEmailTemplateUpdate = {
	subject_line: string
	greetings: string
	variables: {
		for_greetings: TVariable[]
		for_subject_line: TVariable[]
	}
}
